import { useEffect, useState, useCallback, useRef } from "react";
import api, { errMsg, fmtMoney, fmtDate } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, Download, ClipboardEdit, History, Wrench } from "lucide-react";
import { useNavigate } from "react-router-dom";

const EMPTY = { code: "", name: "", model: "", area: "", location: "", tag: "", notes: "", active: true };

export default function Equipment() {
  const { t } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const editable = can("admin", "buyer", "warehouse");
  const fileRef = useRef(null);
  const [pmHist, setPmHist] = useState(null); // { equipment, timeline, ... } or null
  const [pmHistOpen, setPmHistOpen] = useState(false);

  const openPmHistory = async (eq) => {
    try {
      const { data } = await api.get(`/mnt/equipment/${eq.id}/pm-history`);
      setPmHist(data);
      setPmHistOpen(true);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const load = useCallback(() => {
    api.get("/equipment", { params: q ? { q } : {} }).then((r) => setRows(r.data));
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/equipment-template", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = "plantilla_equipos.csv";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const importCsv = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/equipment-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("eq.importDone").replace("{created}", r.data.created).replace("{skipped}", r.data.skipped));
      if (r.data.errors?.length) toast.error(r.data.errors.slice(0, 3).join(" · "));
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    try {
      if (editId) await api.put(`/equipment/${editId}`, form);
      else await api.post("/equipment", form);
      toast.success(t("common.saved"));
      setOpen(false); setForm(EMPTY); setEditId(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (id) => {
    if (!window.confirm(t("eq.confirmDelete"))) return;
    try { await api.delete(`/equipment/${id}`); toast.success(t("common.deleted")); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="equipment-page">
      <PageHeader title={t("eq.title")} subtitle={t("eq.subtitle")}>
        {editable && (
          <div className="flex flex-wrap gap-2 items-center">
            <input ref={fileRef} type="file" accept=".csv" className="hidden"
              onChange={(e) => importCsv(e.target.files?.[0])} data-testid="eq-import-input" />
            <Button size="sm" variant="outline" onClick={downloadTemplate}
              className="rounded-none text-xs font-bold h-8" data-testid="eq-template-button">
              <Download size={13} className="mr-1" /> {t("eq.template")}
            </Button>
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}
              className="rounded-none text-xs font-bold h-8" data-testid="eq-import-button">
              <Upload size={13} className="mr-1" /> {t("eq.import")}
            </Button>
            {can("admin") && (
              <Button size="sm" variant="outline"
                onClick={async () => {
                  if (!window.confirm("¿Crear flotilla estándar de 15 equipos mineros para pruebas?")) return;
                  try {
                    const r = await api.post("/mnt/seed-fleet");
                    toast.success(`Creados: ${r.data.created} · Omitidos: ${r.data.skipped}`);
                    load();
                  } catch (e) { toast.error(errMsg(e)); }
                }}
                className="rounded-none text-xs font-bold h-8" data-testid="eq-seed-fleet">
                <Plus size={13} className="mr-1" /> Cargar flotilla
              </Button>
            )}
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button size="sm" className="rounded-none text-xs font-bold" data-testid="eq-new-button">
                <Plus size={13} className="mr-1" /> {t("eq.new")}
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="uppercase font-black">{editId ? t("common.edit") : t("eq.new")}</DialogTitle>
                <DialogDescription className="text-xs">{t("eq.subtitle")}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("eq.code")} (N° Económico)</Label>
                  <Input value={form.code} disabled={!!editId} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    className="rounded-none h-8 text-xs font-black tabular-nums" data-testid="eq-form-code" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("eq.tag")}</Label>
                  <Input value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })}
                    className="rounded-none h-8 text-xs" data-testid="eq-form-tag" />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("eq.name")}</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="rounded-none h-8 text-xs" data-testid="eq-form-name" />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("eq.model")}</Label>
                  <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })}
                    className="rounded-none h-8 text-xs" placeholder="Ej: CAT 793F, Komatsu HD785…" data-testid="eq-form-model" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("eq.area")}</Label>
                  <Input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })}
                    className="rounded-none h-8 text-xs" data-testid="eq-form-area" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("eq.location")}</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="rounded-none h-8 text-xs" data-testid="eq-form-location" />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("common.notes")}</Label>
                  <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="rounded-none h-8 text-xs" data-testid="eq-form-notes" />
                </div>
                <div className="col-span-2 flex items-center gap-2 pt-2">
                  <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="eq-form-active" />
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("common.active")}</Label>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={save} disabled={!form.code || !form.name}
                  className="rounded-none font-bold text-xs" data-testid="eq-save-button">{t("common.save")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </PageHeader>

      <Input placeholder={t("common.search")} value={q} onChange={(e) => setQ(e.target.value)}
        className="rounded-none h-8 text-xs max-w-64 mb-4" data-testid="eq-search-input" />

      {rows.length === 0 ? <Empty text={t("common.noData")} /> : (
        <div className="tbl-wrap" data-testid="eq-table">
          <table className="tbl">
            <thead><tr>
              <th>{t("eq.code")}</th><th>{t("eq.name")}</th><th>{t("eq.model")}</th><th>{t("eq.tag")}</th>
              <th>{t("eq.area")}</th><th>{t("eq.location")}</th><th>{t("common.active")}</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`eq-row-${r.code}`}>
                  <td className="font-bold tabular-nums">{r.code}</td>
                  <td>{r.name}</td>
                  <td className="text-muted-foreground text-[11px]">{r.model || "—"}</td>
                  <td className="text-muted-foreground text-[11px]">{r.tag || "—"}</td>
                  <td className="text-muted-foreground text-[11px]">{r.area || "—"}</td>
                  <td className="text-muted-foreground text-[11px]">{r.location || "—"}</td>
                  <td>{r.active ? <span className="text-[10px] font-bold text-emerald-600">SÍ</span> : <span className="text-[10px] text-muted-foreground">NO</span>}</td>
                  <td>
                    <div className="flex gap-2">
                      <button className="text-primary hover:opacity-70" data-testid={`eq-io-${r.code}`} title={t("eq.viewInternalOrder")}
                        onClick={() => navigate(`/mnt/equipment/${r.id}/internal-order`)}>
                        <ClipboardEdit size={12} />
                      </button>
                      <button className="text-muted-foreground hover:text-primary" data-testid={`eq-pmhist-${r.code}`} title="Historial de PMs"
                        onClick={() => openPmHistory(r)}>
                        <Wrench size={12} />
                      </button>
                      <button className="text-muted-foreground hover:text-primary" data-testid={`eq-hist-${r.code}`} title={t("nav.mnt.history")}
                        onClick={() => navigate(`/mnt/history?eid=${r.id}`)}>
                        <History size={12} />
                      </button>
                      {editable && (
                        <>
                          <button className="text-muted-foreground hover:text-primary" data-testid={`eq-edit-${r.code}`}
                            onClick={() => { setForm({ ...EMPTY, ...r }); setEditId(r.id); setOpen(true); }}>
                            <Pencil size={12} />
                          </button>
                          {can("admin") && (
                            <button className="text-muted-foreground hover:text-destructive" data-testid={`eq-delete-${r.code}`}
                              onClick={() => del(r.id)}>
                              <Trash2 size={12} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* PM History Modal */}
      <Dialog open={pmHistOpen} onOpenChange={setPmHistOpen}>
        <DialogContent className="rounded-none sm:max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="pm-history-dialog">
          <DialogHeader>
            <DialogTitle className="uppercase font-black">
              Historial de servicios PM — {pmHist?.equipment?.code} · {pmHist?.equipment?.name}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Línea de tiempo de servicios preventivos ejecutados en este equipo.
            </DialogDescription>
          </DialogHeader>
          {pmHist && (
            <>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="border p-3">
                  <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold">Servicios ejecutados</div>
                  <div className="text-2xl font-black tabular-nums" data-testid="pm-hist-count">{pmHist.count}</div>
                </div>
                <div className="border p-3">
                  <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold">Costo acumulado</div>
                  <div className="text-2xl font-black tabular-nums text-emerald-700">{fmtMoney(pmHist.total_cost)}</div>
                </div>
                <div className="border p-3">
                  <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold">Horas de paro</div>
                  <div className="text-2xl font-black tabular-nums">{pmHist.total_downtime.toFixed(1)} h</div>
                </div>
              </div>
              {pmHist.timeline.length === 0 ? (
                <Empty text="Sin servicios PM registrados." />
              ) : (
                <div className="space-y-3" data-testid="pm-hist-timeline">
                  {pmHist.timeline.map((s, i) => (
                    <div key={s.wo_id} className="border-l-4 border-primary bg-card p-3" data-testid={`pm-hist-item-${i}`}>
                      <div className="flex justify-between items-start flex-wrap gap-2">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-black uppercase tabular-nums">{s.wo_number}</span>
                            <span className="text-[10px] font-bold uppercase bg-primary text-primary-foreground px-1.5 py-0.5">{s.pm_code || s.wo_type}</span>
                            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 ${s.wo_status === "closed" ? "bg-emerald-100 text-emerald-700 border border-emerald-300" : "bg-amber-100 text-amber-700 border border-amber-300"}`}>{s.wo_status}</span>
                          </div>
                          <div className="text-xs mt-1 font-bold">{s.pm_name}</div>
                          {s.notif_number && (
                            <div className="text-[10px] text-muted-foreground">Aviso: {s.notif_number}</div>
                          )}
                        </div>
                        <div className="text-right text-[11px]">
                          <div className="font-bold tabular-nums">{s.hourmeter.toFixed(0)} h · horómetro</div>
                          <div className="text-muted-foreground">{fmtDate(s.closed_at || s.actual_end || s.created_at)}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-2 mt-3 text-[10px]">
                        <div><span className="text-muted-foreground uppercase tracking-widest">Técnico:</span> <span className="font-bold">{s.technician || "—"}</span></div>
                        <div><span className="text-muted-foreground uppercase tracking-widest">Supervisor:</span> <span className="font-bold">{s.supervisor || "—"}</span></div>
                        <div><span className="text-muted-foreground uppercase tracking-widest">Paro:</span> <span className="font-bold tabular-nums">{s.downtime_hours.toFixed(1)} h</span></div>
                        <div><span className="text-muted-foreground uppercase tracking-widest">Costo total:</span> <span className="font-bold tabular-nums text-emerald-700">{fmtMoney(s.total_cost)}</span></div>
                      </div>
                      {s.consumed_materials?.length > 0 && (
                        <div className="mt-2 pt-2 border-t">
                          <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Materiales consumidos</div>
                          <table className="w-full text-[10px]">
                            <tbody>
                              {s.consumed_materials.map((m, mi) => (
                                <tr key={mi}>
                                  <td className="font-mono">{m.item_number}</td>
                                  <td>{m.description}</td>
                                  <td className="text-right tabular-nums font-bold">{m.qty} {m.uom}</td>
                                  <td className="text-right tabular-nums text-muted-foreground">{fmtMoney(m.value)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
